/**
 * When the app shell should ask `/api/init` for fresh tokens.
 *
 * The governing constraint: `/api/init` rotates tokens once the access token is
 * inside its pre-expiry window (`ROTATION_LEAD_SECONDS` in
 * `lib/amplify-session-tokens.ts`), or already expired. Before that window a plain
 * `fetchAuthSession` returns the cached token, so an earlier wake-up gets the
 * *same* token back.
 *
 * That matters because every `/api/init` response is pushed into React state as a
 * new `features` object, re-running the effects keyed on it in `app/main.tsx`. A
 * schedule that wakes up before the server will rotate doesn't refresh anything;
 * it just makes the shell poll itself. Both helpers here are aligned to the
 * server's rotation window for that reason.
 *
 * TR-3773: this used to wait for expiry itself (plus 2 s), because the server only
 * rotated after expiry. Every session then held an expired token for a few seconds
 * at each expiry, longer when a background tab throttled the timer, and a chat
 * POST sent in that gap got a 403 "Signature has expired." from the edge authorizer.
 */

import { isTokenDueForRotation, ROTATION_LEAD_SECONDS } from '@/lib/amplify-session-tokens';

/**
 * Wake up this long before `exp`. Must stay inside the server's rotation window
 * (`ROTATION_LEAD_SECONDS`), so the wake-up gets a new token while the old one is
 * still valid. The spare minute absorbs clock skew and a slightly late timer.
 */
export const REFRESH_LEAD_MS = 5 * 60 * 1000;

/** Cushion past `exp` so a rotate-after-expiry request cannot race the expiry itself. */
const POST_EXPIRY_BUFFER_MS = 2_000;

/** Floor on any scheduled delay, so clock skew or a slow response can't hot-loop. */
const MIN_DELAY_MS = 15_000;

/**
 * Retry cadence when a wake-up inside the rotation window came back without a new
 * token (a transient Cognito failure: `/api/init` serves the still-valid token).
 */
const IN_WINDOW_RETRY_MS = 60_000;

/** Treat a token this close to `exp` as due, even when the server won't rotate early. */
const DUE_WINDOW_MS = 10_000;

function readExp(decodedAccessToken: Record<string, unknown> | null | undefined): number | null {
  const exp = decodedAccessToken?.exp;
  return typeof exp === 'number' ? exp : null;
}

/** Whether `/api/init` would rotate this token right now (see `isTokenDueForRotation`). */
function serverWillRotate(decodedAccessToken: Record<string, unknown>, nowMs: number): boolean {
  const iat = decodedAccessToken.iat;
  return isTokenDueForRotation(
    { payload: { exp: readExp(decodedAccessToken) ?? undefined, iat: typeof iat === 'number' ? iat : undefined } },
    Math.floor(nowMs / 1000),
  );
}

/**
 * Delay before the next scheduled `/api/init`.
 *
 * Outside the lead window, wake up `REFRESH_LEAD_MS` before expiry; the server
 * rotates then. A token still inside the window after a wake-up means that
 * rotation failed transiently, so try again in a minute, but never later than
 * expiry. A token too short-lived for early rotation waits for expiry itself, as
 * before. A missing `exp` falls back to an hour, matching the previous behaviour.
 */
export function msUntilRefresh(
  decodedAccessToken: Record<string, unknown> | null | undefined,
  nowMs: number = Date.now(),
): number {
  const exp = readExp(decodedAccessToken);
  const expiresInMs = exp !== null ? exp * 1000 - nowMs : 60 * 60 * 1000;

  if (expiresInMs > REFRESH_LEAD_MS) {
    return expiresInMs - REFRESH_LEAD_MS;
  }
  const untilExpiry = expiresInMs + POST_EXPIRY_BUFFER_MS;
  if (decodedAccessToken && exp !== null && serverWillRotate(decodedAccessToken, nowMs)) {
    return Math.max(Math.min(IN_WINDOW_RETRY_MS, untilExpiry), MIN_DELAY_MS);
  }
  return Math.max(untilExpiry, MIN_DELAY_MS);
}

/**
 * Whether a proactive refresh is due right now. Used by the tab-refocus /
 * visibility / online backup: background tabs throttle `setTimeout`, so the
 * scheduled wake-up can fire late (or not at all) and strand an old token.
 *
 * Due means `/api/init` will hand back a new token: the token is inside the
 * server's rotation window, or expired (or within {@link DUE_WINDOW_MS} of it).
 * A missing `exp` returns false — let the scheduled loop own that case rather
 * than refreshing on every focus event.
 */
export function shouldRefreshNow(
  decodedAccessToken: Record<string, unknown> | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  const exp = readExp(decodedAccessToken);
  if (exp === null || !decodedAccessToken) return false;
  if (exp * 1000 - nowMs <= DUE_WINDOW_MS) return true;
  return exp * 1000 - nowMs <= REFRESH_LEAD_MS && serverWillRotate(decodedAccessToken, nowMs);
}

/** Exported for the invariant test: the client lead must sit inside the server window. */
export const SERVER_ROTATION_LEAD_MS = ROTATION_LEAD_SECONDS * 1000;
